import { InjectionToken } from "@angular/core";
import type { Collector } from "@partikelart/collector/domain";
import type {
	FactoryLayout,
	MonitoringArea,
	TrapLimit,
	TrapMeasurement,
	TrapPosition,
} from "@partikelart/environment/domain";
import type {
	BookTrapCollectorDto,
	CreateMonitoringAreaDto,
	CreateTrapLimitDto,
	CreateTrapMeasurementDto,
	LabReportCommitItemDto,
	LabReportCommitResultDto,
	ParsedLabReportFileDto,
	ParsedSurfaceReportFileDto,
	SurfaceReportCommitItemDto,
	SurfaceReportCommitResultDto,
	UpdateFactoryLayoutDto,
	UpdateMonitoringAreaDto,
	UpdateTrapLimitDto,
	UpdateTrapMeasurementDto,
	UpdateTrapPositionDto,
} from "@partikelart/environment/dtos";
import { Observable } from "rxjs";

/** Position with its parent area and layout, returned by getPositionById. */
export interface PositionLookup {
	position: TrapPosition;
	area: MonitoringArea;
	layout: FactoryLayout;
}

/** A measurement with its position coordinates, returned by getMeasurementsForArea. */
export interface AreaMeasurement extends TrapMeasurement {
	/** Position x coordinate in layout image pixels. */
	x: number;
	/** Position y coordinate in layout image pixels. */
	y: number;
}

/** Payload for creating a measurement at a specific position (positionId is in the URL path). */
export type CreateMeasurementPayload = Omit<CreateTrapMeasurementDto, "trapPositionId">;

/** Payload for creating an area at a specific layout (layoutId is in the URL path). */
export type CreateAreaPayload = Omit<CreateMonitoringAreaDto, "layoutId">;

export interface EnvironmentApiPort {
	// Layouts
	getLayouts(siteId?: string): Observable<FactoryLayout[]>;
	getLayout(id: string): Observable<FactoryLayout>;
	createLayout(name: string, siteId: string, imageFile: File, pixelsPerMeter?: number): Observable<FactoryLayout>;
	updateLayout(id: string, data: UpdateFactoryLayoutDto): Observable<FactoryLayout>;
	deleteLayout(id: string): Observable<void>;

	// Areas
	getAreas(layoutId: string): Observable<MonitoringArea[]>;
	createArea(layoutId: string, payload: CreateAreaPayload): Observable<MonitoringArea>;
	updateArea(areaId: string, payload: UpdateMonitoringAreaDto): Observable<MonitoringArea>;
	deleteArea(areaId: string): Observable<void>;

	// Positions
	getPositions(areaId: string): Observable<TrapPosition[]>;
	getPositionById(id: string): Observable<PositionLookup>;
	updatePosition(positionId: string, payload: UpdateTrapPositionDto): Observable<TrapPosition>;
	confirmPosition(positionId: string): Observable<TrapPosition>;
	activatePosition(positionId: string): Observable<TrapPosition>;
	deactivatePosition(positionId: string): Observable<TrapPosition>;
	deletePosition(positionId: string): Observable<void>;
	suggestPositions(areaId: string, payload: { spacingM: number }): Observable<TrapPosition[]>;

	// Collectors
	getActiveCollectors(): Observable<Collector[]>;
	bookCollector(positionId: string, payload: BookTrapCollectorDto): Observable<TrapPosition>;

	// Measurements
	createMeasurement(positionId: string, payload: CreateMeasurementPayload): Observable<TrapMeasurement>;
	getMeasurements(positionId: string): Observable<TrapMeasurement[]>;
	getMeasurementsForArea(areaId: string): Observable<AreaMeasurement[]>;
	updateMeasurement(measurementId: string, payload: UpdateTrapMeasurementDto): Observable<TrapMeasurement>;
	deleteMeasurement(measurementId: string): Observable<void>;

	// Lab report import
	parseLabReports(siteId: string, files: File[]): Observable<ParsedLabReportFileDto[]>;
	commitLabReportImport(siteId: string, items: LabReportCommitItemDto[]): Observable<LabReportCommitResultDto[]>;

	// Surface report import (Jomesa PicEd ASCII)
	parseSurfaceReports(siteId: string, files: File[]): Observable<ParsedSurfaceReportFileDto[]>;
	commitSurfaceReportImport(
		siteId: string,
		items: SurfaceReportCommitItemDto[],
	): Observable<SurfaceReportCommitResultDto[]>;

	// Limits
	createLimit(payload: CreateTrapLimitDto): Observable<TrapLimit>;
	getLimitsForArea(areaId: string): Observable<TrapLimit[]>;
	updateLimit(limitId: string, payload: UpdateTrapLimitDto): Observable<TrapLimit>;
	deleteLimit(limitId: string): Observable<void>;
}

export const ENVIRONMENT_API = new InjectionToken<EnvironmentApiPort>("EnvironmentApiPort");

export interface EnvironmentApiConfig {
	getPortalBackendUrl(): string;
}

export const ENVIRONMENT_API_CONFIG = new InjectionToken<EnvironmentApiConfig>("ENVIRONMENT_API_CONFIG");
